/**
 * Candidate rows behind a `picker` filter.
 *
 * A separate hook from `useSearch` because it is a different query with a
 * different lifetime: it runs only while one picker panel is open, its input is
 * that panel's own text, and its failure belongs to the panel rather than to a
 * result section. What it shares is the discipline — debounce, latest-run-wins,
 * and a failure that stays visible instead of reading as "no candidates".
 */
import type { ResolvedSearchFilter, SearchFilterOption } from '@hibro/shared';
import { parseSearchFilterOptions } from '@hibro/shared';
import { useEffect, useRef, useState } from 'react';

/** Keystroke settling time, matching `useSearch`'s. */
const DEBOUNCE_MS = 120;

/** Rows fetched per query. A picker panel is short; well above what it draws. */
const OPTIONS_LIMIT = 50;

export interface FilterOptionsState {
  options: readonly SearchFilterOption[];
  loading: boolean;
  /** Set when the options command failed; the panel shows it rather than an empty list. */
  error: Error | null;
}

export function useFilterOptions(
  filter: ResolvedSearchFilter | null,
  query: string,
  executeCommand: (id: string, ...args: unknown[]) => Promise<unknown>,
): FilterOptionsState {
  const [state, setState] = useState<FilterOptionsState>({
    options: [],
    loading: false,
    error: null,
  });
  const runIdRef = useRef(0);
  const command = filter?.type === 'picker' ? filter.optionsCommand : undefined;

  useEffect(() => {
    if (!command) {
      setState({ options: [], loading: false, error: null });
      return;
    }
    const runId = (runIdRef.current += 1);
    setState((current) => ({ ...current, loading: true }));

    const timer = setTimeout(() => {
      void (async () => {
        try {
          const raw = await executeCommand(command, {
            query,
            limit: OPTIONS_LIMIT,
          });
          if (runIdRef.current !== runId) {
            return;
          }
          // Throws on a malformed answer, which surfaces below as a visible
          // error. Degrading to an empty list would make a broken module look
          // like one that simply has no candidates (见 `no-implicit-fallbacks`).
          setState({
            options: parseSearchFilterOptions(raw),
            loading: false,
            error: null,
          });
        } catch (error) {
          if (runIdRef.current !== runId) {
            return;
          }
          setState({
            options: [],
            loading: false,
            error: error instanceof Error ? error : new Error(String(error)),
          });
        }
      })();
    }, DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [command, query, executeCommand]);

  return state;
}
